import { PermissionEnum } from '@Application/Security/Roles';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import FACILITY_SECTIONS from '@UI/Routing/facilitySections';
import SectionLanding from '@UI/Routing/SectionLanding';
import FacilitiesPage from '@UI/Pages/Agencies/Facilities';
import type * as React from 'react';

/**
 * Entry point of the Agences section, mounted at `/facilities`. The path now names a screen of its own
 * (the agency list), so it cannot be a pure `SectionLanding` resolver: a holder of `FACILITY_READ` must
 * see the list directly, without a redirect round-trip.
 *
 * A caller without `FACILITY_READ` (but with `STAFF_READ`, granted by the route's union guard) falls
 * back to `SectionLanding` rather than a hardcoded `<Navigate to="/facilities/staff-members" />`: the
 * catalogue stays the single source of truth, so a third section added later needs no change here. This
 * fallback never loops — it is only reached when `FACILITY_READ` is missing, so `SectionLanding` skips
 * the `/facilities` entry (not accessible to this caller) and resolves to the next one. If none were
 * accessible, the route's own union guard would already have refused access before this component ever
 * renders.
 */
const FacilitySectionEntry: React.FC = () => {
	const canReadFacilities = useHasPermission(PermissionEnum.FACILITY_READ);

	if (canReadFacilities) {
		return <FacilitiesPage />;
	}

	return (
		<SectionLanding
			sections={FACILITY_SECTIONS}
			emptyMessage="Vous n'avez accès à aucun écran de cette section."
		/>
	);
};

export default FacilitySectionEntry;
